import { useState, type FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useClient, useUpdateClient } from '../api/queries/clients';
import { useOrganization, useUpdateOrganization } from '../api/queries/organizations';
import { useLookups, activeOptionsFor, useLookupLabel } from '../api/queries/lookups';
import { LookupSelect } from '../components/LookupSelect';
import { MoneyInput } from '../components/MoneyInput';
import { useProjects } from '../api/queries/projects';
import { useInvoices } from '../api/queries/invoices';
import { PhoneListInput, type PhoneEntry } from '../components/PhoneListInput';
import { PhoneInput } from '../components/PhoneInput';
import { AddressFields } from '../components/AddressFields';
import { OrgPicker } from '../components/OrgPicker';
import { PersonPicker } from '../components/PersonPicker';
import { BillToBlock } from '../components/BillToBlock';
import { PersonAvatar } from '../components/PersonAvatar';
import {
  addressValueFromColumns,
  addressValueToColumns,
  formatAddressColumns,
  emptyAddressValue,
  type AddressValue,
} from '../utils/address';
import { DateInput } from '../components/DateInput';
import { IconAction } from '../components/IconAction';
import { formatPhoneDisplay, stripPhoneForStorage, formatPhoneAsTyped } from '../utils/phone';
import { fmtDate } from '../utils/date';
import {
  useRetainers,
  useCreateRetainer,
  useDrawRetainer,
} from '../api/queries/retainers';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import type { ClientStatus } from '@stfors/shared-types';
import './ClientDetailPage.css';

const PROJECT_STATUS_LABEL: Record<string, string> = {
  new: 'New',
  in_progress: 'In Progress',
  analysis: 'Analysis',
  reporting: 'Reporting',
  closed: 'Closed',
};

const INVOICE_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void',
};

import { formatMoney } from '../utils/money';

export function ClientDetailPage(): JSX.Element {
  const { id: idRaw } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const idNum = idRaw ? Number(idRaw) : NaN;
  const id = Number.isInteger(idNum) && idNum > 0 ? idNum : null;

  const clientQuery = useClient(id);
  const updateClient = useUpdateClient(id ?? 0);
  // The representative saves on pick rather than behind an Edit/Save pair —
  // it is two dropdowns, and the block below has to re-render to be useful.
  const [repError, setRepError] = useState<string | null>(null);
  const canWrite = can('client.write');
  const saveRep = async (patch: { rep_organization_id?: number | null; rep_person_id?: number | null }) => {
    setRepError(null);
    try {
      await updateClient.mutateAsync(patch);
    } catch (err) {
      setRepError(err instanceof ApiError && err.status === 403
        ? "You don't have permission to change this client."
        : 'Could not save the representative. Try again.');
    }
  };
  // For a company client, load the linked org so we can list its contacts in
  // the "change primary contact" picker. Disabled (no fetch) for individuals.
  const orgId =
    clientQuery.data?.client.kind === 'company' ? clientQuery.data.client.organization_id : null;
  const orgQuery = useOrganization(orgId);
  const updateOrg = useUpdateOrganization();
  const lookups = useLookups();
  const titleOptions = activeOptionsFor(lookups.data?.lookup_options, 'title');
  const suffixOptions = activeOptionsFor(lookups.data?.lookup_options, 'suffix');
  const phoneTypeOptions = activeOptionsFor(lookups.data?.lookup_options, 'phone_type');
  const statusLabel = useLookupLabel('client_status');
  const projectsQuery = useProjects(id !== null ? { client_id: id } : {});
  const invoicesQuery = useInvoices(id !== null ? { client_id: id } : {});
  const retainersQuery = useRetainers(id !== null ? { client_id: id } : {});
  const createRetainer = useCreateRetainer();
  const drawRetainer = useDrawRetainer();
  const [statusError, setStatusError] = useState<string | null>(null);

  const [showNewRetainer, setShowNewRetainer] = useState(false);
  const [retainerAmount, setRetainerAmount] = useState('');
  // Default effective date: today (local midnight).
  const [retainerEffective, setRetainerEffective] = useState<number | null>(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
  });
  const [retainerNotes, setRetainerNotes] = useState('');
  const [retainerError, setRetainerError] = useState<string | null>(null);

  const [drawingId, setDrawingId] = useState<number | null>(null);
  const [drawAmount, setDrawAmount] = useState('');
  const [drawNotes, setDrawNotes] = useState('');
  const [drawError, setDrawError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<{
    title: string;
    first_name: string;
    last_name: string;
    suffix: string;
    company_name: string;
    email: string;
    industry: string;
    notes: string;
    tags: string;
    // Company-kind only: the organization's own contact fields.
    company_email: string;
    company_phone: string;   // formatted display form
  }>({
    title: '', first_name: '', last_name: '', suffix: '',
    company_name: '', email: '', industry: '',
    notes: '', tags: '',
    company_email: '', company_phone: '',
  });
  // Structured addresses: company branch edits the linked org's address; the
  // individual branch edits the client's own structured columns.
  const [companyAddr, setCompanyAddr] = useState<AddressValue>(emptyAddressValue());
  const [individualAddr, setIndividualAddr] = useState<AddressValue>(emptyAddressValue());
  // Phones edited as a separate piece of state — array shape, formatted
  // display values. Mapped to/from c.phones (10-digit storage) on
  // load and submit.
  const [editPhones, setEditPhones] = useState<PhoneEntry[]>([]);
  const [editError, setEditError] = useState<string | null>(null);

  const onCreateRetainer = async (e: FormEvent) => {
    e.preventDefault();
    if (id === null) return;
    setRetainerError(null);
    const amt = retainerAmount.trim();
    if (!/^\d+(\.\d{1,2})?$/.test(amt) || Number(amt) <= 0) {
      setRetainerError('Enter a positive amount with up to 2 decimal places.');
      return;
    }
    if (retainerEffective === null) {
      setRetainerError('Pick an effective date.');
      return;
    }
    try {
      await createRetainer.mutateAsync({
        client_id: id,
        initial_amount: amt,
        effective_from: retainerEffective,
        notes: retainerNotes.trim() || undefined,
      });
      setShowNewRetainer(false);
      setRetainerAmount('');
      setRetainerNotes('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setRetainerError("You don't have permission to create retainers.");
        return;
      }
      setRetainerError('Could not create retainer. Try again.');
    }
  };

  const onDrawRetainer = async (e: FormEvent, retainerId: number) => {
    e.preventDefault();
    setDrawError(null);
    const amt = drawAmount.trim();
    if (!/^\d+(\.\d{1,2})?$/.test(amt) || Number(amt) <= 0) {
      setDrawError('Enter a positive amount with up to 2 decimal places.');
      return;
    }
    try {
      await drawRetainer.mutateAsync({
        id: retainerId,
        amount: amt,
        notes: drawNotes.trim() || undefined,
      });
      setDrawingId(null);
      setDrawAmount('');
      setDrawNotes('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setDrawError('Draw exceeds remaining balance.');
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setDrawError("You don't have permission to draw against retainers.");
        return;
      }
      setDrawError('Could not record draw. Try again.');
    }
  };

  const startEdit = () => {
    if (!clientQuery.data) return;
    const c = clientQuery.data.client;
    setEditForm({
      title: c.title ?? '',
      first_name: c.first_name ?? '',
      last_name: c.last_name ?? '',
      suffix: c.suffix ?? '',
      company_name: c.company_name ?? '',
      email: c.email ?? '',
      industry: c.industry ?? '',
      notes: c.notes ?? '',
      tags: c.tags.join(', '),
      company_email: c.company_email ?? '',
      company_phone: c.company_phone ? formatPhoneAsTyped(c.company_phone) : '',
    });
    // Company address comes from the linked org's structured columns; the
    // individual's from the client's own columns. (Both expose address_line1/2,
    // which addressValueFromColumns reads natively.)
    setCompanyAddr(addressValueFromColumns(orgQuery.data?.organization));
    setIndividualAddr(addressValueFromColumns(c));
    setEditPhones(
      (c.phones ?? []).map((p) => ({
        phone_type: p.phone_type,
        phone: formatPhoneAsTyped(p.phone),
      })),
    );
    setEditError(null);
    setEditing(true);
  };

  const onSaveEdit = async (e: FormEvent) => {
    e.preventDefault();
    setEditError(null);
    const isCompanyClient = clientQuery.data?.client.kind === 'company';
    const tagsPayload = editForm.tags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    try {
      if (isCompanyClient) {
        // Company branch: write the linked organization's own fields + the
        // engagement fields. Name parts + the contact's phones/email live on
        // the person and are edited on /people/:id.
        if (editForm.company_name.trim().length === 0) {
          setEditError('Company name is required.');
          return;
        }
        // Structured address → the linked organization (single source of
        // truth); clear the legacy free-text blob so the org page doesn't
        // double-display. Then the rest of the company fields via the client.
        if (orgId !== null) {
          const cols = addressValueToColumns(companyAddr);
          await updateOrg.mutateAsync({
            id: orgId,
            address_line1: cols.line1, address_line2: cols.line2, city: cols.city,
            state: cols.state, postal_code: cols.postal_code, country: cols.country ?? undefined,
            address: null,
          });
        }
        await updateClient.mutateAsync({
          company_name: editForm.company_name.trim(),
          industry: editForm.industry.trim() || null,
          company_email: editForm.company_email.trim() || null,
          company_phone: stripPhoneForStorage(editForm.company_phone) || null,
          notes: editForm.notes.trim() || null,
          tags: tagsPayload,
        });
        setEditing(false);
        return;
      }

      // Individual branch: the client IS a person — the flat form is correct.
      if (editForm.last_name.trim().length === 0) {
        setEditError('Last name is required.');
        return;
      }
      const phonesPayload = editPhones
        .map((p) => ({
          phone_type: p.phone_type,
          phone: stripPhoneForStorage(p.phone) ?? '',
        }))
        .filter((p) => p.phone.length === 10 && p.phone_type.length > 0);

      const indCols = addressValueToColumns(individualAddr);
      await updateClient.mutateAsync({
        title: editForm.title.trim() || null,
        first_name: editForm.first_name.trim() || null,
        last_name: editForm.last_name.trim(),
        suffix: editForm.suffix.trim() || null,
        email: editForm.email.trim() || null,
        phones: phonesPayload,
        address_line1: indCols.line1, address_line2: indCols.line2, city: indCols.city,
        state: indCols.state, postal_code: indCols.postal_code, country: indCols.country,
        notes: editForm.notes.trim() || null,
        tags: tagsPayload,
      });
      setEditing(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setEditError("You don't have permission to edit this client.");
        return;
      }
      if (err instanceof ApiError && err.status === 400) {
        setEditError('Validation failed. Check email format.');
        return;
      }
      setEditError('Could not save. Try again.');
    }
  };

  const onChangeStatus = async (newStatus: ClientStatus) => {
    setStatusError(null);
    try {
      await updateClient.mutateAsync({ status: newStatus });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setStatusError("You don't have permission to update this client.");
        return;
      }
      setStatusError('Could not update status. Try again.');
    }
  };

  // Reassign a company client's primary contact (company branch only). The API
  // keeps organization_contacts.is_primary in sync.
  const onChangePrimaryContact = async (personId: number | null) => {
    setStatusError(null);
    try {
      await updateClient.mutateAsync({ primary_contact_person_id: personId });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setStatusError("You don't have permission to update this client.");
        return;
      }
      setStatusError('Could not change the primary contact. Try again.');
    }
  };

  if (id === null) return <p className="cd-status cd-error">Invalid client id.</p>;
  if (clientQuery.isPending) return <p className="cd-status">Loading…</p>;
  if (clientQuery.error) {
    const err = clientQuery.error;
    if (err instanceof ApiError && err.status === 404) {
      return <p className="cd-status cd-error">Client not found.</p>;
    }
    if (err instanceof ApiError && err.status === 403) {
      return <p className="cd-status cd-error">You don't have permission to view this client.</p>;
    }
    return <p className="cd-status cd-error">Failed to load.</p>;
  }

  const c = clientQuery.data!.client;
  const isCompany = c.kind === 'company';
  const orgContacts = orgQuery.data?.organization.contacts ?? [];

  // Company info is presented from the linked organization (the directory
  // entry) as the single source of truth; the client projections are only a
  // fallback while the org query loads (they carry the same values).
  const orgData = orgQuery.data?.organization;
  const companyIndustry = orgData?.industry ?? c.industry;
  const companyEmail = orgData?.email ?? c.company_email;
  const companyPhone = orgData?.phone ?? c.company_phone;
  const companyAddressLine = formatAddressColumns(orgData) ?? c.company_address;
  // The primary contact's display name + primary phone, for the contact card.
  const contactName =
    [c.title, c.first_name, c.last_name].filter(Boolean).join(' ') +
    (c.suffix ? `, ${c.suffix}` : '');
  const contactPrimaryPhone = c.phones.find((p) => p.is_primary) ?? c.phones[0];
  const projects = projectsQuery.data?.projects ?? [];
  const invoices = invoicesQuery.data?.invoices ?? [];
  const retainers = retainersQuery.data?.retainers ?? [];

  const retainersTotalBalance = retainers.reduce(
    (sum, r) => sum + Number(r.current_balance),
    0,
  );
  const retainersInitialTotal = retainers.reduce(
    (sum, r) => sum + Number(r.initial_amount),
    0,
  );

  const outstanding = invoices
    .filter((i) => i.status === 'sent' || i.status === 'overdue')
    .reduce((acc, i) => acc + Number(i.total), 0);

  return (
    <div className="cd-page">
      <header className="cd-header">
        <button className="btn-text" onClick={() => navigate('/clients')}>
          ← All clients
        </button>
        <h2>
          <span className="cd-title">{c.name}</span>
        </h2>
        {can('client.write') ? (
          <LookupSelect
            category="client_status"
            className={`cd-status-select status-${c.status}`}
            value={c.status}
            onChange={onChangeStatus}
            disabled={updateClient.isPending}
            aria-label="Client status"
          />
        ) : (
          <span className={`status-pill status-${c.status}`}>
            {statusLabel(c.status)}
          </span>
        )}
        {can('client.write') && !editing && (
          <IconAction action="edit" onClick={startEdit} className="cd-edit-btn" />
        )}
      </header>

      {statusError && (
        <p className="cd-status-error" role="alert">{statusError}</p>
      )}

      {editing ? (
        isCompany ? (
        // ---------- Company edit form (writes the linked organization) ----------
        <form className="cd-edit-form" onSubmit={onSaveEdit}>
          <div className="cd-edit-row">
            <label className="cd-edit-field cd-edit-field-wide">
              <span>Company name *</span>
              <input
                type="text"
                className="form-input"
                value={editForm.company_name}
                onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                maxLength={200}
                autoComplete="organization"
                required
                disabled={updateClient.isPending}
              />
            </label>
            <label className="cd-edit-field">
              <span>Industry</span>
              <input
                type="text"
                className="form-input"
                value={editForm.industry}
                onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })}
                disabled={updateClient.isPending}
              />
            </label>
          </div>
          <div className="cd-edit-row">
            <label className="cd-edit-field">
              <span>Office email</span>
              <input
                type="email"
                className="form-input"
                value={editForm.company_email}
                onChange={(e) => setEditForm({ ...editForm, company_email: e.target.value })}
                disabled={updateClient.isPending}
              />
            </label>
            <label className="cd-edit-field">
              <span>Office phone</span>
              <PhoneInput
                className="form-input"
                value={editForm.company_phone}
                onChange={(v) => setEditForm({ ...editForm, company_phone: v })}
                disabled={updateClient.isPending}
              />
            </label>
          </div>
          <div className="cd-edit-field cd-edit-field-wide">
            <span>Office address</span>
            <AddressFields
              value={companyAddr}
              onChange={setCompanyAddr}
              legacyText={orgQuery.data?.organization.address}
              autoParse
              disabled={updateClient.isPending || updateOrg.isPending}
              idPrefix="Office address"
            />
          </div>
          <label className="cd-edit-field cd-edit-field-wide">
            <span>Tags (comma-separated)</span>
            <input
              type="text"
              className="form-input"
              value={editForm.tags}
              onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
              placeholder="e.g. legal-firm, retained, vip"
              disabled={updateClient.isPending}
            />
          </label>
          <label className="cd-edit-field cd-edit-field-wide">
            <span>Notes</span>
            <textarea
              className="form-input cd-edit-textarea"
              value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              rows={3}
              disabled={updateClient.isPending}
            />
          </label>
          <p className="cd-meta-label">
            The contact's name, personal email &amp; mobile live on their own
            profile — edit them from the Primary contact card.
          </p>
          {editError && <p className="cd-status-error" role="alert">{editError}</p>}
          <div className="cd-edit-actions">
            <IconAction
              action="cancel"
              onClick={() => setEditing(false)}
              disabled={updateClient.isPending}
            />
            <IconAction
              action="save"
              type="submit"
              disabled={updateClient.isPending || editForm.company_name.trim().length === 0}
              label={updateClient.isPending ? 'Saving…' : 'Save'}
            />
          </div>
        </form>
        ) : (
        // ---------- Individual edit form (the client IS a person) ----------
        <form className="cd-edit-form" onSubmit={onSaveEdit}>
          <div className="cd-edit-row">
            <label className="cd-edit-field cd-edit-field-narrow">
              <span>Title</span>
              <select
                className="form-input"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                autoComplete="honorific-prefix"
                disabled={updateClient.isPending}
              >
                <option value="">—</option>
                {titleOptions.map((o) => (
                  <option key={o.id} value={o.value}>{o.value}</option>
                ))}
                {/* Legacy value not in current options — keep selectable
                    so admins can see it before they normalize. */}
                {editForm.title && !titleOptions.some((o) => o.value === editForm.title) && (
                  <option value={editForm.title}>{editForm.title} (legacy)</option>
                )}
              </select>
            </label>
            <label className="cd-edit-field">
              <span>First name</span>
              <input
                type="text"
                className="form-input"
                value={editForm.first_name}
                onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                maxLength={100}
                autoComplete="given-name"
                disabled={updateClient.isPending}
              />
            </label>
            <label className="cd-edit-field">
              <span>Last name *</span>
              <input
                type="text"
                className="form-input"
                value={editForm.last_name}
                onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                required
                maxLength={100}
                autoComplete="family-name"
                disabled={updateClient.isPending}
              />
            </label>
            <label className="cd-edit-field cd-edit-field-narrow">
              <span>Suffix</span>
              <select
                className="form-input"
                value={editForm.suffix}
                onChange={(e) => setEditForm({ ...editForm, suffix: e.target.value })}
                autoComplete="honorific-suffix"
                disabled={updateClient.isPending}
              >
                <option value="">—</option>
                {suffixOptions.map((o) => (
                  <option key={o.id} value={o.value}>{o.value}</option>
                ))}
                {editForm.suffix && !suffixOptions.some((o) => o.value === editForm.suffix) && (
                  <option value={editForm.suffix}>{editForm.suffix} (legacy)</option>
                )}
              </select>
            </label>
          </div>
          <label className="cd-edit-field cd-edit-field-wide">
            <span>Email</span>
            <input
              type="email"
              className="form-input"
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              disabled={updateClient.isPending}
            />
          </label>
          <div className="cd-edit-field cd-edit-field-wide">
            <span>Phone numbers</span>
            <PhoneListInput
              value={editPhones}
              onChange={setEditPhones}
              typeOptions={phoneTypeOptions}
              disabled={updateClient.isPending}
            />
          </div>
          <div className="cd-edit-field cd-edit-field-wide">
            <span>Address</span>
            <AddressFields
              value={individualAddr}
              onChange={setIndividualAddr}
              legacyText={clientQuery.data?.client.address}
              autoParse
              disabled={updateClient.isPending}
              idPrefix="Address"
            />
          </div>
          <label className="cd-edit-field cd-edit-field-wide">
            <span>Tags (comma-separated)</span>
            <input
              type="text"
              className="form-input"
              value={editForm.tags}
              onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
              placeholder="e.g. legal-firm, retained, vip"
              disabled={updateClient.isPending}
            />
          </label>
          <label className="cd-edit-field cd-edit-field-wide">
            <span>Notes</span>
            <textarea
              className="form-input cd-edit-textarea"
              value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              rows={3}
              disabled={updateClient.isPending}
            />
          </label>
          {editError && <p className="cd-status-error" role="alert">{editError}</p>}
          <div className="cd-edit-actions">
            <IconAction
              action="cancel"
              onClick={() => setEditing(false)}
              disabled={updateClient.isPending}
            />
            <IconAction
              action="save"
              type="submit"
              disabled={updateClient.isPending || editForm.last_name.trim().length === 0}
              label={updateClient.isPending ? 'Saving…' : 'Save'}
            />
          </div>
        </form>
        )
      ) : (
      isCompany ? (
      // ---------- Company view: company info + primary contact card ----------
      <section className="cd-meta">
        <div>
          <div className="cd-meta-label">Industry</div>
          <div className="cd-meta-value">{companyIndustry ?? '—'}</div>
        </div>
        <div>
          <div className="cd-meta-label">Office email</div>
          <div className="cd-meta-value">
            {companyEmail ? <a href={`mailto:${companyEmail}`}>{companyEmail}</a> : '—'}
          </div>
        </div>
        <div>
          <div className="cd-meta-label">Office phone</div>
          <div className="cd-meta-value">
            {companyPhone
              ? <a href={`tel:${companyPhone}`}>{formatPhoneDisplay(companyPhone)}</a>
              : '—'}
          </div>
        </div>
        {companyAddressLine && (
          <div className="cd-meta-wide">
            <div className="cd-meta-label">Office address</div>
            <div className="cd-meta-value cd-address">
              {companyAddressLine}
            </div>
          </div>
        )}
        {c.tags.length > 0 && (
          <div className="cd-meta-wide">
            <div className="cd-meta-label">Tags</div>
            <div className="cd-meta-value cd-tags">
              {c.tags.map((t) => (
                <span key={t} className="cd-tag">{t}</span>
              ))}
            </div>
          </div>
        )}
        <div className="cd-meta-wide" style={{ marginTop: 'var(--space-2)' }}>
          <div className="cd-meta-label">Primary contact</div>
          {c.primary_contact_person_id !== null ? (
            <div className="cd-meta-value" style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
              <PersonAvatar personId={c.primary_contact_person_id} name={contactName} size={44} />
              <div>
              <div><strong>{contactName}</strong></div>
              {c.primary_contact_email && (
                <div><a href={`mailto:${c.primary_contact_email}`}>{c.primary_contact_email}</a></div>
              )}
              {contactPrimaryPhone && (
                <div>
                  <span className="cd-phone-type">{contactPrimaryPhone.phone_type}</span>{' '}
                  <a href={`tel:${contactPrimaryPhone.phone}`}>{formatPhoneDisplay(contactPrimaryPhone.phone)}</a>
                </div>
              )}
              <div style={{ marginTop: 'var(--space-1)' }}>
                <Link to={`/people/${c.primary_contact_person_id}`} className="btn-text">Edit profile →</Link>
              </div>
              </div>
            </div>
          ) : (
            <div className="cd-meta-value">No primary contact set yet.</div>
          )}
          {can('client.write') && orgContacts.length > 0 && (
            <label className="cd-meta-value" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
              <span className="cd-meta-label">Change primary contact</span>
              <select
                className="form-input"
                value={c.primary_contact_person_id ?? ''}
                onChange={(e) => onChangePrimaryContact(e.target.value ? Number(e.target.value) : null)}
                disabled={updateClient.isPending}
              >
                <option value="">— none —</option>
                {orgContacts.map((ct) => (
                  <option key={ct.id} value={ct.person_id}>
                    {ct.display_name ?? ct.person?.display_name ?? 'Contact'}{ct.title ? ` · ${ct.title}` : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          {c.organization_id !== null && (
            <div style={{ marginTop: 'var(--space-2)' }}>
              <Link to={`/organizations/${c.organization_id}`} className="btn-text">
                Manage contacts &amp; full company details →
              </Link>
            </div>
          )}
        </div>
      </section>
      ) : (
      // ---------- Individual view: the client IS a person ----------
      <section className="cd-meta">
        <div>
          <div className="cd-meta-label">Email</div>
          <div className="cd-meta-value">
            {c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : '—'}
          </div>
        </div>
        <div>
          <div className="cd-meta-label">Phone{c.phones.length > 1 ? 's' : ''}</div>
          <div className="cd-meta-value">
            {c.phones.length === 0 ? '—' : (
              <ul className="cd-phone-list">
                {c.phones.map((p) => (
                  <li key={p.id}>
                    <span className="cd-phone-type">{p.phone_type}</span>
                    <a href={`tel:${p.phone}`}>{formatPhoneDisplay(p.phone)}</a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {(formatAddressColumns(c) ?? c.address) && (
          <div className="cd-meta-wide">
            <div className="cd-meta-label">Address</div>
            <div className="cd-meta-value cd-address">{formatAddressColumns(c) ?? c.address}</div>
          </div>
        )}
        {c.tags.length > 0 && (
          <div className="cd-meta-wide">
            <div className="cd-meta-label">Tags</div>
            <div className="cd-meta-value cd-tags">
              {c.tags.map((t) => (
                <span key={t} className="cd-tag">{t}</span>
              ))}
            </div>
          </div>
        )}
      </section>
      )
      )}

      {!editing && c.notes && (
        <section className="cd-description">
          <h3>Notes</h3>
          <p>{c.notes}</p>
        </section>
      )}

      <section className="cd-block">
        <header className="cd-block-header">
          <h3>Invoicing</h3>
          <span className="cd-block-meta">
            How invoices to this client are addressed. New matters start here.
          </span>
        </header>
        <div className="cd-rep">
          <div className="cd-rep-pickers">
            <label className="cd-rep-field">
              <span>Represented by (law firm)</span>
              <OrgPicker
                value={c.rep_organization_id ?? null}
                onChange={(orgId) => void saveRep({ rep_organization_id: orgId })}
                disabled={!canWrite || updateClient.isPending}
              />
            </label>
            <label className="cd-rep-field">
              <span>Attorney</span>
              <PersonPicker
                value={c.rep_person_id ?? null}
                onChange={(personId) => void saveRep({ rep_person_id: personId })}
                disabled={!canWrite || updateClient.isPending}
                placeholder="Search people…"
                allowCreate={can('organization.write')}
              />
            </label>
          </div>
          <BillToBlock
            lines={c.bill_to}
            label="Default Bill To"
            empty="Pick a firm and attorney to address invoices care of them."
          />
          {repError && <p className="cd-error" role="alert">{repError}</p>}
        </div>
      </section>

      <section className="cd-block">
        <header className="cd-block-header">
          <h3>Retainers</h3>
          <span className="cd-block-meta">
            {retainers.length} retainer{retainers.length === 1 ? '' : 's'}
            {retainers.length > 0 && (
              <> · {formatMoney(retainersTotalBalance.toFixed(2))} of {formatMoney(retainersInitialTotal.toFixed(2))} remaining</>
            )}
          </span>
          {can('invoice.write') && !showNewRetainer && (
            <button
              type="button"
              className="btn-cyan-primary cd-section-btn"
              onClick={() => setShowNewRetainer(true)}
            >
              New retainer
            </button>
          )}
        </header>

        {showNewRetainer && (
          <form className="cd-retainer-form" onSubmit={onCreateRetainer}>
            <div className="cd-retainer-form-row">
              <label className="cd-retainer-field">
                <span>Amount (USD)</span>
                <MoneyInput
                  value={retainerAmount}
                  onChange={setRetainerAmount}
                  placeholder="5000.00"
                  disabled={createRetainer.isPending}
                />
              </label>
              <label className="cd-retainer-field">
                <span>Effective from</span>
                <DateInput
                  value={retainerEffective}
                  onChange={setRetainerEffective}
                  disabled={createRetainer.isPending}
                />
              </label>
            </div>
            <label className="cd-retainer-field">
              <span>Notes</span>
              <input
                type="text"
                className="form-input"
                value={retainerNotes}
                onChange={(e) => setRetainerNotes(e.target.value)}
                placeholder="Optional — e.g. wire received from Smith Holdings"
                maxLength={10_000}
                disabled={createRetainer.isPending}
              />
            </label>
            {retainerError && <p className="cd-status-error" role="alert">{retainerError}</p>}
            <div className="cd-retainer-form-actions">
              <button
                type="button"
                className="btn-charcoal-secondary"
                onClick={() => {
                  setShowNewRetainer(false);
                  setRetainerError(null);
                }}
                disabled={createRetainer.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-cyan-primary"
                disabled={createRetainer.isPending || retainerAmount.trim().length === 0}
              >
                {createRetainer.isPending ? 'Saving…' : 'Create retainer'}
              </button>
            </div>
          </form>
        )}

        {retainersQuery.isPending ? (
          <p className="cd-block-empty">Loading…</p>
        ) : retainers.length === 0 ? (
          <p className="cd-block-empty">No retainers on this client.</p>
        ) : (
          <ul className="cd-retainer-list">
            {retainers.map((r) => {
              const initial = Number(r.initial_amount);
              const balance = Number(r.current_balance);
              const drawn = Math.max(0, initial - balance);
              const pctRemaining = initial > 0 ? (balance / initial) * 100 : 0;
              const depleted = balance <= 0;
              const isDrawing = drawingId === r.id;
              return (
                <li
                  key={r.id}
                  className={`cd-retainer ${depleted ? 'cd-retainer-depleted' : ''}`}
                >
                  <div className="cd-retainer-row">
                    <div className="cd-retainer-amounts">
                      <div className="cd-retainer-balance">{formatMoney(r.current_balance)}</div>
                      <div className="cd-retainer-of">
                        of {formatMoney(r.initial_amount)} initial · {formatMoney(drawn.toFixed(2))} drawn
                      </div>
                    </div>
                    <div className="cd-retainer-meta">
                      <div>Effective {fmtDate(r.effective_from)}</div>
                      {r.project_case_number && (
                        <div className="cd-retainer-project">
                          Project <code>{r.project_case_number}</code>
                        </div>
                      )}
                    </div>
                    {can('invoice.write') && !depleted && !isDrawing && (
                      <button
                        type="button"
                        className="btn-charcoal-secondary cd-retainer-draw-btn"
                        onClick={() => {
                          setDrawingId(r.id);
                          setDrawAmount('');
                          setDrawNotes('');
                          setDrawError(null);
                        }}
                      >
                        Draw
                      </button>
                    )}
                  </div>
                  <div className="cd-retainer-bar">
                    <div
                      className="cd-retainer-bar-fill"
                      style={{ width: `${Math.min(100, Math.max(0, pctRemaining))}%` }}
                    />
                  </div>
                  {r.notes && (
                    <p className="cd-retainer-notes">{r.notes}</p>
                  )}
                  {isDrawing && (
                    <form
                      className="cd-retainer-draw"
                      onSubmit={(e) => onDrawRetainer(e, r.id)}
                    >
                      <MoneyInput
                        className="cd-retainer-draw-amount"
                        value={drawAmount}
                        onChange={setDrawAmount}
                        placeholder={`up to ${Number(r.current_balance).toFixed(2)}`}
                        disabled={drawRetainer.isPending}
                        autoFocus
                      />
                      <input
                        type="text"
                        className="form-input cd-retainer-draw-notes"
                        value={drawNotes}
                        onChange={(e) => setDrawNotes(e.target.value)}
                        placeholder="Optional note (e.g. INV-2026-A4K7M2X9-02)"
                        disabled={drawRetainer.isPending}
                      />
                      <button
                        type="submit"
                        className="btn-cyan-primary cd-retainer-draw-submit"
                        disabled={drawRetainer.isPending || drawAmount.trim().length === 0}
                      >
                        {drawRetainer.isPending ? 'Drawing…' : 'Confirm'}
                      </button>
                      <button
                        type="button"
                        className="btn-charcoal-secondary cd-retainer-draw-cancel"
                        onClick={() => {
                          setDrawingId(null);
                          setDrawError(null);
                        }}
                        disabled={drawRetainer.isPending}
                      >
                        Cancel
                      </button>
                      {drawError && (
                        <p className="cd-status-error cd-retainer-draw-error" role="alert">{drawError}</p>
                      )}
                    </form>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="cd-block">
        <header className="cd-block-header">
          <h3>Projects</h3>
          <span className="cd-block-meta">{projects.length} projects</span>
        </header>
        {projectsQuery.isPending ? (
          <p className="cd-block-empty">Loading…</p>
        ) : projects.length === 0 ? (
          <p className="cd-block-empty">No projects for this client yet.</p>
        ) : (
          <table className="cd-table">
            <thead>
              <tr>
                <th>Case #</th>
                <th>Title</th>
                <th>Type</th>
                <th>Status</th>
                <th>Priority</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr
                  key={p.id}
                  className="cd-row"
                  onClick={() => navigate(`/projects/${p.id}`)}
                >
                  <td><code>{p.case_number}</code></td>
                  <td className="cd-cell-desc">{p.title}</td>
                  <td>{p.case_type.replace(/_/g, ' ')}</td>
                  <td>
                    <span className={`status-pill status-${p.status}`}>
                      {PROJECT_STATUS_LABEL[p.status] ?? p.status}
                    </span>
                  </td>
                  <td>P{p.priority}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="cd-block">
        <header className="cd-block-header">
          <h3>Invoices</h3>
          <span className="cd-block-meta">
            {invoices.length} invoices
            {outstanding > 0 && (
              <> · {formatMoney(outstanding.toFixed(2))} outstanding</>
            )}
          </span>
        </header>
        {invoicesQuery.isPending ? (
          <p className="cd-block-empty">Loading…</p>
        ) : invoices.length === 0 ? (
          <p className="cd-block-empty">No invoices for this client.</p>
        ) : (
          <table className="cd-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Issued</th>
                <th>Due</th>
                <th className="cd-numeric">Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr
                  key={inv.id}
                  className="cd-row"
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                >
                  <td><code>{inv.invoice_number}</code></td>
                  <td>{fmtDate(inv.issue_date)}</td>
                  <td>{fmtDate(inv.due_date)}</td>
                  <td className="cd-numeric">{formatMoney(inv.total)}</td>
                  <td>
                    <span className={`iv-pill iv-status-${inv.status}`}>
                      {INVOICE_STATUS_LABEL[inv.status] ?? inv.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
